import { CallPreferencesRingTonesSelector } from './CallPreferencesRingtonesSelector.container';

import { FormItem, Grid, Spacer, Typography } from '@aircall/tractor';
import { CountryPrefixPicker } from '@components/CountryPrefixPicker';
import { GridItem, GridLayout } from '@components/GridLayout/GridLayout';
import { PaperForm } from '@components/PaperForm/PaperForm';
import { UnitInput } from '@components/UnitInput';
import { pipeValidators, validateMaxValue, validateMinValue } from '@dashboard/library';
import { useUpdateCallPreferences } from '@hooks/useUpdateCallPreferences';
import { UserProfileTabProps } from '@pages/user-profile/UserProfile.decl';
import { TFunction } from 'i18next';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';

export interface ICallPreferencesForm {
  useAircallPhoneNumberAsCallerID: boolean;
  defaultPrefix: string;
  currentRingtone: string;
  wrapUpTime: number;
  wrapUpTimeMinutes: string;
  wrapUpTimeSeconds: string;
}

export const hasWrapupTimeError = (
  wrapUpTimeMinutesError?: string,
  wrapUpTimeSecondsError?: string
) => !!wrapUpTimeMinutesError || !!wrapUpTimeSecondsError;

export const getValidationStatus = (
  wrapUpTimeMinutesError?: string,
  wrapUpTimeSecondsError?: string
) => {
  if (hasWrapupTimeError(wrapUpTimeMinutesError, wrapUpTimeSecondsError)) {
    return 'error';
  }

  return undefined;
};

export const getWrapUpTImeValidationMessage =
  (t: TFunction) => (wrapUpTimeMinutesError?: string, wrapUpTimeSecondsError?: string) => {
    if (hasWrapupTimeError(wrapUpTimeMinutesError, wrapUpTimeSecondsError)) {
      return t((wrapUpTimeMinutesError || wrapUpTimeSecondsError) as string);
    }

    return undefined;
  };

export function CallPreferencesSection({ user }: UserProfileTabProps) {
  const { t } = useTranslation();
  const { onSubmit, initialFormValues } = useUpdateCallPreferences({ user });

  return (
    <PaperForm<ICallPreferencesForm>
      formProps={{ onSubmit, initialValues: initialFormValues }}
      shouldBlockNavigation
      title={t('user_profile.call_preferences.title')}
    >
      {({ values, errors }) => (
        <GridLayout gridColumnGap={{ _: 'm', md: 'l' }} gridRowGap='m'>
          <GridItem xs={12} md={6}>
            <Field name='defaultPrefix'>
              {({ input: { onChange, value } }) => (
                <Spacer space='xxs' direction='vertical' fluid>
                  <Typography variant='body' as='label' data-test='test-label'>
                    {t('user_profile.call_preferences.form.default_prefix.field')}
                  </Typography>
                  <CountryPrefixPicker
                    name='defaultPrefix'
                    defaultSelected={value}
                    onChange={onChange}
                    data-test='user-profile-default-prefix-select'
                    placeholder={t('user_profile.call_preferences.form.default_prefix.placeholder')}
                  />
                </Spacer>
              )}
            </Field>
          </GridItem>
          <GridItem xs={12} md={6} lg={6}>
            <FormItem label={t('user_profile.call_preferences.form.current_ringtone.field')}>
              <Field name='currentRingtone'>
                {({ input: { onChange } }) => (
                  <CallPreferencesRingTonesSelector
                    width='100%'
                    onChange={onChange}
                    defaultSelected={values.currentRingtone}
                  />
                )}
              </Field>
            </FormItem>
          </GridItem>
          <GridItem gridRow={{ _: 3, md: 2 }} xs={12} md={6} lg={6}>
            <FormItem
              label={t('user_profile.call_preferences.form.wrap_up_time.field')}
              name='wrapUpTime'
              validationStatus={getValidationStatus(
                errors?.wrapUpTimeMinutes,
                errors?.wrapUpTimeSeconds
              )}
              helpText={getWrapUpTImeValidationMessage(t)(
                errors?.wrapUpTimeMinutes,
                errors?.wrapUpTimeSeconds
              )}
            >
              <Grid gridTemplateColumns='repeat(2,1fr)' gridGap='s'>
                <Field
                  name='wrapUpTimeMinutes'
                  validate={pipeValidators(validateMinValue(0), validateMaxValue(59))}
                >
                  {({ input: { onChange, value } }) => (
                    <UnitInput
                      name='wrapUpTimeMinutes'
                      unit={t('user_profile.call_preferences.form.wrap_up_time.units.minutes')}
                      value={value}
                      validationStatus={getValidationStatus(
                        errors?.wrapUpTimeMinutes,
                        errors?.wrapUpTimeSeconds
                      )}
                      type='number'
                      onChange={onChange}
                      min={0}
                      data-test='user-profile-wrap-up-time-minute-unit-input'
                    />
                  )}
                </Field>
                <Field
                  name='wrapUpTimeSeconds'
                  validate={pipeValidators(validateMinValue(0), validateMaxValue(59))}
                >
                  {({ input: { onChange, value } }) => (
                    <UnitInput
                      name='wrapUpTimeSeconds'
                      type='number'
                      validationStatus={getValidationStatus(
                        errors?.wrapUpTimeMinutes,
                        errors?.wrapUpTimeSeconds
                      )}
                      unit={t('user_profile.call_preferences.form.wrap_up_time.units.seconds')}
                      value={value}
                      onChange={onChange}
                      min={0}
                      max={59}
                      data-test='user-profile-wrap-up-time-second-unit-input'
                    />
                  )}
                </Field>
              </Grid>
            </FormItem>
          </GridItem>
        </GridLayout>
      )}
    </PaperForm>
  );
}
